/**
 * ZKnowledge - Zod Validation Schemas for the Knowledge chat-attachment
 * upload surface (`/api/knowledge/uploadChatAttachments*`).
 *
 * Controller: knowledge/knowledge.controller.ts
 *
 * Scope note: the legacy `knowledge.service.ts` api-client exposed a large
 * surface (sitemap/url verification, knowledge CRUD, knowers, folders), but the
 * only hooks with live consumers are the two multipart chat-attachment uploads
 * migrated here. The remaining hooks were dead (0 importers) and retire with the
 * legacy file. This manifest can be extended if any of those routes gain a
 * consumer again.
 */

import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";

/**
 * Result of a chat-attachment upload batch. Each attachment resolves to a
 * discriminated union on `status`: a fully-populated success entry ("DONE") or
 * an error entry ("ERROR"). Modelled with a `z.custom` passthrough — this is the
 * service's own rich return projection, not a stable field-by-field wire
 * contract, so runtime validation stays a no-op and preserves the exact payload
 * the legacy hook delivered.
 */
export type KnowledgeChatAttachmentUploadResult = {
  success: boolean;
  attachments: Array<
    | {
        id: string;
        name: string;
        type: string;
        size: number;
        url: string;
        thumbnailUrl?: string;
        isImage: boolean;
        status: "DONE";
        metadata: {
          width?: number;
          height?: number;
          mimeType: string;
          thumbnailUrl?: string;
        };
      }
    | {
        id: string;
        name: string;
        status: "ERROR";
        error: {
          message: string;
          code: string;
        };
      }
  >;
  summary: {
    total: number;
    succeeded: number;
    failed: number;
  };
};

/**
 * Query params shared by both upload routes. The backend reads these via
 * `@Query`, so this schema types the request side only (no runtime parse on the
 * generated client).
 */
const UploadChatAttachmentsQuerySchema = z.object({
  generateThumbnails: z.boolean().optional(),
  maxThumbnailSize: z.number().optional(),
  skipTextExtraction: z.boolean().optional()
});

export const ZKnowledge = {
  /**
   * POST /api/knowledge/uploadChatAttachments — multipart upload of one or more
   * chat attachments. Body is FormData (multer parses the `files` field
   * server-side); returns per-file upload results.
   */
  UploadChatAttachments: {
    method: "POST" as const,
    path: "/api/knowledge/uploadChatAttachments",
    Body: z.custom<FormData>(),
    Query: UploadChatAttachmentsQuerySchema,
    Response: ApiSuccessSchema(z.custom<KnowledgeChatAttachmentUploadResult>())
  },

  /**
   * POST /api/knowledge/uploadChatAttachmentsForCue — Cue
   * variant: identical shape, but the route bypasses the MIME allow-list.
   */
  UploadChatAttachmentsForCue: {
    method: "POST" as const,
    path: "/api/knowledge/uploadChatAttachmentsForCue",
    Body: z.custom<FormData>(),
    Query: UploadChatAttachmentsQuerySchema,
    Response: ApiSuccessSchema(z.custom<KnowledgeChatAttachmentUploadResult>())
  }
} as const;
